/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {firstValueFrom, forkJoin, switchMap} from 'rxjs';
import {PassportService} from '../../../../services/passport.service';
import {
  InteractionChangeItem,
  ProcessedChangeItem,
} from '../../../../models/InteractionHistoryChange';
import {TranslateService} from '@ngx-translate/core';
import {GeneralDataHandlingService} from '../../../../services/general.data-handling.service';
import {FormCategory} from '../../../../models/FormCategory';
import {PassportDto} from '../../../../models/Passport';
import {PassportTranslationService} from '../../../../services/passport-translation.service';

@Component({
  selector: 'app-battery-interaction-differences',
  templateUrl: './battery-interaction-differences.component.html',
  styleUrl: './battery-interaction-differences.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatteryInteractionDifferencesComponent implements OnInit {
  private readonly passportService = inject(PassportService);
  private readonly dataHandlingService = inject(GeneralDataHandlingService);
  private readonly translate = inject(TranslateService);
  private readonly passportTranslation = inject(PassportTranslationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  @Input() passport?: PassportDto;
  @Input() originId? = '';
  @Input() targetId = '';

  loading = true;

  interactionChanges?: InteractionChangeItem[];
  schema: FormCategory[];

  processedChanges: ProcessedChangeItem[] = [];

  notMaintainableFields = [
    'materials.materials.cathode_anode_electrolyte.0.type',
    'materials.components.0.hazardous_substances.0.substance.category',
    'compliance.symbols.lead_symbol.name',
    'compliance.symbols.lead_symbol.description',
    'compliance.symbols.cadmium_symbol.name',
    'compliance.symbols.cadmium_symbol.description',
    'compliance.symbols.collection_symbol.name',
    'compliance.symbols.collection_symbol.description',
    'compliance.conformity.test_report_results.name',
    'compliance.conformity.test_report_results.description',
    'compliance.conformity.declaration_of_conformity_eu.name',
    'compliance.conformity.declaration_of_conformity_eu.description',
    'circularity.safety_requirements.safety_instructions.name',
    'circularity.safety_requirements.safety_instructions.description',
    'circularity.design_for_circularity.spare_parts_source.name',
    'circularity.design_for_circularity.spare_parts_source.address.number',
    'circularity.design_for_circularity.spare_parts_source.address.street',
    'circularity.design_for_circularity.spare_parts_source.address.premise',
    'circularity.design_for_circularity.spare_parts_source.address.post_code',
    'circularity.design_for_circularity.spare_parts_source.address.country_sn',
    'circularity.design_for_circularity.spare_parts_source.address.postal_delivery_point',
    'circularity.design_for_circularity.spare_parts_source.contact.fax_number',
    'circularity.design_for_circularity.spare_parts_source.contact.phone_number',
    'circularity.design_for_circularity.part_numbers_components.name',
    'circularity.design_for_circularity.part_numbers_components.description',
    'circularity.design_for_circularity.manual_removal_from_appliance.name',
    'circularity.design_for_circularity.manual_removal_from_appliance.description',
    'circularity.design_for_circularity.manual_disassembly_of_battery_pack.name',
    'circularity.design_for_circularity.manual_disassembly_of_battery_pack.description',
    'circularity.end_of_life_information.information_on_recycling_operations.name',
    'circularity.end_of_life_information.information_on_recycling_operations.description',
    'circularity.end_of_life_information.role_of_end_users_to_waste_prevention.name',
    'circularity.end_of_life_information.role_of_end_users_to_waste_prevention.description',
    'circularity.end_of_life_information.role_of_end_users_to_separate_collection.name',
    'circularity.end_of_life_information.role_of_end_users_to_separate_collection.description',
    'performance.negative_events.information_on_accidents.name',
    'performance.negative_events.information_on_accidents.description',
    'performance.internal_resistance.current_resistance.modules.0.cells.0.cell_id',
    'performance.internal_resistance.current_resistance.modules.0.module_id',
    'performance.internal_resistance.current_resistance.pack_id',
    'performance.internal_resistance.initial_resistance.modules.0.cells.0.cell_id',
    'performance.internal_resistance.initial_resistance.modules.0.module_id',
    'performance.internal_resistance.initial_resistance.pack_id',
    'performance.internal_resistance.resistance_increase.modules.0.cells.0.cell_id',
    'performance.internal_resistance.resistance_increase.modules.0.module_id',
    'performance.internal_resistance.resistance_increase.pack_id',
    'carbon_footprint.web_link_to_carbon_footprint_study.name',
    'carbon_footprint.web_link_to_carbon_footprint_study.description',
    'general_information.identification.manufacturer_id.address.number',
    'general_information.identification.manufacturer_id.address.street',
    'general_information.identification.manufacturer_id.address.premise',
    'general_information.identification.manufacturer_id.address.locality',
    'general_information.identification.manufacturer_id.address.post_code',
    'general_information.identification.manufacturer_id.address.country_sn',
    'general_information.identification.manufacturer_id.address.postal_delivery_point',
    'general_information.identification.manufacturer_id.contact.email',
    'general_information.identification.manufacturer_id.contact.website',
    'general_information.identification.manufacturer_id.contact.fax_number',
    'general_information.identification.manufacturer_id.contact.phone_number',
    'general_information.identification.manufacturing_place.number',
    'general_information.identification.manufacturing_place.street',
    'general_information.identification.manufacturing_place.premise',
    'general_information.identification.manufacturing_place.post_code',
    'general_information.identification.manufacturing_place.country_sn',
    'general_information.identification.manufacturing_place.postal_delivery_point',
    'supply_chain_due_diligence.additional_voluntary.sustainability_report.name',
    'supply_chain_due_diligence.additional_voluntary.sustainability_report.description',
    'supply_chain_due_diligence.additional_voluntary.eu_tax_disclosure_statement.name',
    'supply_chain_due_diligence.additional_voluntary.eu_tax_disclosure_statement.description',
    'supply_chain_due_diligence.additional_voluntary.third_party_supply_chain_assurances.name',
    'supply_chain_due_diligence.additional_voluntary.third_party_supply_chain_assurances.description',
    'supply_chain_due_diligence.information_of_due_diligence_report.name',
    'supply_chain_due_diligence.information_of_due_diligence_report.description',
  ];

  private readonly metadataFields: string[] = [
    'ancestorPassportId',
    'name',
    'stateCode',
  ];

  async ngOnInit(): Promise<void> {
    this.loading = true;
    if (!this.originId) {
      this.originId = this.targetId;
    }
    const schemaId = this.passport?.metadata?.schemaId || '';
    this.interactionChanges =
      await this.passportService.interactionHistoryChanges(
        this.passport?.id ?? '',
        this.originId,
        this.targetId,
      );
    this.schema = await this.passportService.loadMappedSchema(
      schemaId,
      this.passport?.id,
    );
    await firstValueFrom(
      this.passportTranslation.load(schemaId, this.translate.currentLang),
    );
    this.recomputeProcessedChanges();
    this.loading = false;
    this.cdr.markForCheck();

    this.translate.onLangChange
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(({lang}) =>
          forkJoin([
            this.passportService.loadMappedSchema$(schemaId, this.passport?.id),
            this.passportTranslation.load(schemaId, lang),
          ]),
        ),
      )
      .subscribe(
        // translation load is for its side effect (populating PassportTranslationService cache)
        ([schema]) => {
          this.schema = schema;
          this.recomputeProcessedChanges();
          this.cdr.markForCheck();
        },
      );
  }

  private recomputeProcessedChanges(): void {
    this.processedChanges = (this.interactionChanges ?? [])
      .filter(
        (changeItem) =>
          !this.notMaintainableFields.includes(changeItem.key.join('.')),
      )
      .map((changeItem) => {
        const path = changeItem.key.join('.');
        return {
          change: changeItem,
          path: path,
          label: this.mapAndTranslatePath(path),
        };
      });
  }

  mapAndTranslatePath(path: string): string {
    let subPath = path;
    if (this.metadataFields.includes(subPath)) {
      const translatedValue = this.translate.instant(`battery_pass.${subPath}`);
      return translatedValue !== `battery_pass.${subPath}`
        ? translatedValue
        : `battery_pass.${subPath}`;
    }

    const schemaId = this.passport?.metadata?.schemaId || '';
    while (subPath) {
      const formFieldAttribute = this.searchForAttribute(subPath);
      if (formFieldAttribute) {
        return this.resolveFieldLabel(
          formFieldAttribute.translation.label,
          schemaId,
        );
      }
      subPath = subPath.split('.').slice(1).join('.');
    }
    return path;
  }

  private resolveFieldLabel(key: string, schemaId: string): string {
    const translated = this.passportTranslation.translate(key, schemaId);
    if (translated !== key) {
      return translated;
    }
    const i18nFallback = this.translate.instant(key);
    return i18nFallback !== key ? i18nFallback : translated;
  }

  searchForAttribute(path: string) {
    return this.dataHandlingService.findAttributeWithValue(
      this.schema,
      path,
      'valuePath',
    )[0];
  }
}
